import React, { useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { bool } from 'prop-types';
import classNames from 'classnames';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
  IconButton,
  Input,
  Typography
} from '@one-thd/sui-atomic-components';
import { ArrowForward, Close } from '@one-thd/sui-icons';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useAssistant } from './AssistantProvider';
import { AssistanIcon } from '../../assets/AssistanIcon';
import { dataModel } from './maaDataModel';
import { AssistantDrawer } from './subcomponents/drawer/AssistantDrawer';
import { AssistantHeader } from './subcomponents/drawer/AssistantHeader';
import { AssistantWithPortal } from './subcomponents/AssistantWithPortal';
import { SuggestedQuestions } from './subcomponents/SuggestedQuestions';
import { USER_QUESTION_CHARACTER_LIMIT, keyPressMap } from './constants';
import { getRandomElements } from './subcomponents/utils';
import { GENERAL_QUESTIONS, PIP_QUESTIONS } from './subcomponents/constants';
import { getPageType } from '../../utils/assistant-context-utils';
import { getPortalRootNode, getStickyATCElement } from '../../utils/assistant-utils';
import { AssistantFAB } from './subcomponents/AssistantFAB';
import { DrawerContent } from './subcomponents/DrawerContent';
import { ScrollToBottomButton } from './subcomponents/drawer/ScrollToBottomButton';
import { useDrawer } from './DrawerProvider';
import { getSessionId } from '../../utils/session-utils';

export const MagicApronAssistant = ({ withPortal }) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const pageType = useMemo(() => getPageType(), []);
  const {
    isHistoryAvailable,
    messages,
    historyLoading,
    clearHistory,
    askUserQuestion,
    botLoading: botResponseLoading,
    drawerShouldOpenOnLoad,
    hasSessionInteractedWithFab,
    updateSession
  } = useAssistant();
  const { onScroll, setScrollElRef } = useDrawer();

  const [userMessage, setUserMessage] = useState('');
  const [isMounted, setIsMounted] = useState(false);
  const [historyCleared, setHistoryCleared] = useState(false);
  const [portalRootNode, setPortalRootNode] = useState(null);
  const [focusInput, setFocusInput] = useState(false);
  const [offsetMargin, setOffsetMargin] = useState(0);
  const [drawerState, setDrawerState] = useState('CLOSED');

  // TODO: Initial drawerState is based on channel (mobile vs desktop) & current page type (Homepage, PIP, PEP, etc.)
  // TODO: drawerState must be persited throughout the session.
  // TODO: For mobile, minimized drawer appears on PIP and PLP after a defined delay, not on page load.
  // TODO: Ask for clarification about having a time out screen if it sits for 30min.
  const isOpenDrawerPage = pageType === 'PIP' || pageType === 'PLP';
  const initialOpenDrawerState = isMobile ? 'MINIMIZED' : 'OPEN';
  /**
   * @type {[DrawerStatus, React.Dispatch<React.SetStateAction<DrawerStatus>>]}
   */

  useEffect(() => {
    if (drawerShouldOpenOnLoad && isOpenDrawerPage) {
      setDrawerState(initialOpenDrawerState);
    }
  }, [isHistoryAvailable]);

  const isPIP = typeof window !== 'undefined' ? pageType === 'PIP' : false;
  const randomSuggestQuestions = useMemo(() => getRandomElements(isPIP ? PIP_QUESTIONS : GENERAL_QUESTIONS), []);
  const suggestedQuestions = messages[messages.length - 1]?.content?.suggestedQuestions || randomSuggestQuestions;

  const openDrawer = () => {
    setDrawerState('OPEN');
    updateSession({
      shouldOpenDrawer: true
    });
  };
  const closeDrawer = () => {
    setDrawerState('CLOSED');
    updateSession({
      shouldOpenDrawer: false
    });
  };

  const getOffset = () => {
    const stickyATCElement = getStickyATCElement();
    return stickyATCElement && !stickyATCElement.classList.contains('sui-hidden') ? stickyATCElement.offsetHeight : 0;
  };

  useEffect(() => {
    setIsMounted(true);
    return (() => setIsMounted(false));
  }, []);

  useEffect(() => {
    const targetNode = document.querySelector('#root [data-fusion-section="F"]');

    const callback = () => {
      setOffsetMargin(getOffset());
    };

    const observer = new MutationObserver(callback);
    if (targetNode) {
      observer.observe(targetNode, {
        attributes: true,
        childList: true,
        subtree: true
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    if (withPortal && isMounted) {
      const element = getPortalRootNode(isMobile);
      setPortalRootNode(element);
    }
  }, [isMobile, withPortal, isMounted]);

  const clearPrompt = () => setUserMessage('');

  const handleMessageSubmit = () => {
    if (userMessage.trim() === '' || botResponseLoading || historyLoading) return;

    askUserQuestion(userMessage);
    clearPrompt();
  };

  const onUserQuestionChange = (event) => {
    if (!(keyPressMap.Enter)) setUserMessage(event.target.value);
  };

  const onUserQuestionKeyPress = (event) => {
    // Keeps track of all currently pressed keys
    keyPressMap[event.key] = event.type === 'keydown';

    if (keyPressMap.Enter) {
      keyPressMap.Enter = false;
      handleMessageSubmit();
    }
  };

  const isMobileWithPortal = isMobile && withPortal;

  const onFabClick = () => {
    if (hasSessionInteractedWithFab) {
      setFocusInput(false);
      openDrawer();
    } else {
      window.LIFE_CYCLE_EVENT_BUS.trigger('magic-apron-assistant.fab-click', {
        sessionID: getSessionId(),
      });
      setFocusInput(false);
      setDrawerState('OPEN');
      updateSession({
        shouldOpenDrawer: true,
        hasSessionInteractedWithFab: true
      });
    }
  };

  const classes = classNames('hfapp',
    {
      'sui-z-max sui-w-full sui-absolute sui-bottom-full': isMobileWithPortal,
      'sui-bottom-0 sui-ml-3 sui-my-3 sui-right-0': !isMobileWithPortal,
    }
  );

  const fabContainerClasses = classNames(
    'sui-z-max sui-bottom-4 sui-right-2',
    {
      'sui-absolute': isMobileWithPortal,
      'sui-fixed xl:sui-bottom-10 xl:sui-right-10': !(isMobileWithPortal),
      'sui-hidden': drawerState === 'OPEN',
    }
  );

  const fullContent = () => {
    return (
      <>
        <AssistantHeader
          onClose={() => {
            setFocusInput(false);
            closeDrawer();
          }}
          title={(
            <DrawerTitle
              disableGutters
              ornament={<AssistanIcon height="24px" width="24px" />}
            >
              <div className="sui-flex sui-justify-center sui-align-center">
                <span>Magic Apron </span>
                <Typography variant="body-xs" color="medium" component="span">
                  <span className="sui-align-super sui-pl-1">Beta</span>
                </Typography>
              </div>
            </DrawerTitle>
          )}
          onReset={async () => {
            clearHistory();
            setHistoryCleared(true);
          }}
        />
        <DrawerBody
          overflow="scroll"
          style={{ overscrollBehavior: 'contain' }}
          data-testid="drawer-body"
          ref={setScrollElRef}
          onScroll={onScroll}
        >
          <DrawerContent
            drawerState={drawerState}
            historyCleared={historyCleared}
            setDrawerState={setDrawerState}
          />
        </DrawerBody>
        <DrawerFooter>
          <ScrollToBottomButton />
          <div data-testid="input-box" className="sui-flex sui-justify-between sui-items-center sui-gap-2">
            <Input
              id="qa-bot-input"
              value={userMessage}
              onChange={onUserQuestionChange}
              onKeyDown={onUserQuestionKeyPress}
              onKeyUp={onUserQuestionKeyPress}
              autoFocus={focusInput}
              fullWidth
              multiline
              // based on how many lines are created by max character limit
              maxRows={8}
              placeholder="Ask a question"
              inputAttributes={{
                maxLength: USER_QUESTION_CHARACTER_LIMIT,
                resize: 'none',
                'aria-label': 'Assistant textbox',
              }}
              endAdornment={!!userMessage.length && (
                <IconButton
                  icon={Close}
                  size="small"
                  onClick={clearPrompt}
                  aria-label="Clear question"
                  key="clear-button"
                />
              )}
            />
            <div className="sui-grow-0">
              <Button
                variant="primary"
                onClick={handleMessageSubmit}
                id="magic_apron_qa_find_it_for_me"
                key="submit-button"
                aria-label="Submit question"
              >
                <ArrowForward color="inverse" />
              </Button>
            </div>
          </div>
        </DrawerFooter>
      </>
    );
  };

  const renderAssistant = () => {
    return (
      <ErrorBoundary name="magic-apron-assistant">
        <div
          id="magic-apron-assistant"
          data-testid="magic-apron-assistant"
          className={classes}
          style={{ gridRow: '1 / 3', gridColumn: '2', marginRight: '4.0rem' }}
        >
          <div className={fabContainerClasses}>
            <AssistantFAB onClick={onFabClick} />
          </div>
          {isHistoryAvailable && (
            <AssistantDrawer
              drawerState={drawerState}
              setDrawerState={setDrawerState}
              onClose={() => updateSession({
                shouldOpenDrawer: false
              })}
              isMobile={isMobile}
            >
              {drawerState === 'MINIMIZED' && (
                <DrawerBody className="sui-bg-primary sui-px-4 sui-shrink sui-basis-auto sui-overflow-y-auto">
                  <SuggestedQuestions
                    questions={suggestedQuestions}
                    onClick={async (question) => {
                      setFocusInput(false);
                      askUserQuestion(question);
                      if (drawerState !== 'OPEN') setDrawerState('OPEN');
                    }}
                    withAskPrompt
                    drawerState={drawerState}
                    handleAskPromptClick={() => {
                      setFocusInput(true);
                      openDrawer();
                    }}
                    truncate
                  />
                </DrawerBody>
              )}
              {drawerState === 'OPEN' && (fullContent())}
            </AssistantDrawer>
          )}
        </div>
      </ErrorBoundary>
    );
  };

  return isMounted ? (
    <>
      {withPortal ? portalRootNode !== null && createPortal(
        <ErrorBoundary name="magic-apron-assistant-with-portal">
          <AssistantWithPortal
            isMobile={isMobile}
            offset={offsetMargin}
          >

            {renderAssistant()}
          </AssistantWithPortal>
        </ErrorBoundary>, portalRootNode)
        : renderAssistant()}
    </>
  ) : null;
};

MagicApronAssistant.displayName = 'MagicApronAssistant';

MagicApronAssistant.dataModel = dataModel;

MagicApronAssistant.propTypes = {
  withPortal: bool,
};

MagicApronAssistant.defaultProps = {
  withPortal: true,
};
